import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { loadMenuItems } from '../services/menuService';

const BrandSelectPage: React.FC = () => {
  const { tableNumber } = useParams<{ tableNumber: string }>();
  const navigate = useNavigate();
  const { lang, setLang, t } = useLanguage();
  const [mounted, setMounted] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const sheetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    loadMenuItems().catch(() => {});
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Subtle parallax based on cursor / device tilt
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setTilt({ x, y });
    };
    const onOri = (e: DeviceOrientationEvent) => {
      const x = Math.max(-1, Math.min(1, (e.gamma || 0) / 25));
      const y = Math.max(-1, Math.min(1, (e.beta || 0) / 25));
      setTilt({ x, y });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('deviceorientation', onOri, true);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('deviceorientation', onOri, true);
    };
  }, []);

  const go = (b: 'sushiki' | 'donvito') => {
    navigate(`/table/${tableNumber}/menu/${b}`);
  };

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        overflowX: 'hidden',
        background: '#05070a',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        color: '#f4f4f0',
      }}
    >
      {/* Google Fonts */}
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@500;700;800&family=Inter:wght@400;500;600;700&family=Playfair+Display:ital,wght@1,600&display=swap"
      />

      <style>{`
        @keyframes bsOrbA { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(30px,-40px,0) scale(1.15); } }
        @keyframes bsOrbB { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(-40px,30px,0) scale(1.08); } }
        @keyframes bsOrbC { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(20px,20px,0) scale(1.2); } }
        @keyframes bsGrid { 0% { background-position: 0 0; } 100% { background-position: 80px 80px; } }
        @keyframes bsShine { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes bsFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes bsFadeUp { from { opacity: 0; transform: translate3d(0,24px,0); } to { opacity: 1; transform: translate3d(0,0,0); } }
        @keyframes bsFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bsBadgePulse { 0%,100% { box-shadow: 0 0 0 0 rgba(148,232,21,.35); } 50% { box-shadow: 0 0 0 14px rgba(148,232,21,0); } }
        @keyframes bsRingRotate { to { transform: rotate(360deg); } }

        .bs-card { position: relative; cursor: pointer; transition: transform .55s cubic-bezier(.22,1,.36,1), box-shadow .55s; will-change: transform; }
        .bs-card::before {
          content: '';
          position: absolute; inset: 0;
          border-radius: inherit;
          padding: 1.5px;
          background: var(--bs-ring);
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor; mask-composite: exclude;
          opacity: .55;
          transition: opacity .5s;
        }
        .bs-card:hover::before { opacity: 1; }
        .bs-card .bs-shine {
          position: absolute; inset: 0; border-radius: inherit; pointer-events: none;
          background: linear-gradient(120deg, transparent 35%, rgba(255,255,255,.08) 50%, transparent 65%);
          background-size: 250% 100%;
          opacity: 0;
          transition: opacity .4s;
        }
        .bs-card:hover .bs-shine { opacity: 1; animation: bsShine 1.6s linear infinite; }
        .bs-card:hover { transform: perspective(1100px) rotateX(3deg) rotateY(-3deg) translateY(-10px) scale(1.02); }
        .bs-card:hover .bs-art { transform: scale(1.1) rotate(-3deg); }
        .bs-card .bs-art { transition: transform .7s cubic-bezier(.22,1,.36,1); }
        .bs-card:active { transform: perspective(1100px) translateY(-4px) scale(.99); }

        .bs-cta { transition: background .3s, color .3s, transform .3s; }
        .bs-card:hover .bs-cta { background: var(--bs-accent); color: #0a0a0a; transform: translateX(6px); }

        @media (max-width: 768px) {
          .bs-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
          .bs-card { min-height: 240px !important; padding: 22px !important; }
          .bs-art { height: 90px !important; margin-bottom: 16px !important; }
          .bs-hero-h1 { font-size: clamp(1.9rem, 8vw, 2.6rem) !important; }
          .bs-topbar-pill { font-size: .7rem !important; padding: 6px 10px !important; }
        }

        @media (prefers-reduced-motion: reduce) {
          .bs-card, .bs-card .bs-art, .bs-orb { animation: none !important; transition: none !important; }
        }
      `}</style>

      {/* ═══════════════ 3D BACKGROUND LAYER ═══════════════ */}
      {/* Animated color-mesh orbs */}
      <div
        aria-hidden
        className="bs-orb"
        style={{
          position: 'absolute',
          top: '-15%', left: '-10%',
          width: 680, height: 680,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(148,232,21,.55) 0%, rgba(148,232,21,0) 65%)',
          filter: 'blur(80px)',
          animation: 'bsOrbA 14s ease-in-out infinite',
          transform: `translate3d(${tilt.x * -30}px, ${tilt.y * -30}px, 0)`,
          transition: 'transform .4s ease',
        }}
      />
      <div
        aria-hidden
        className="bs-orb"
        style={{
          position: 'absolute',
          bottom: '-20%', right: '-15%',
          width: 760, height: 760,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(232,169,75,.48) 0%, rgba(232,169,75,0) 65%)',
          filter: 'blur(90px)',
          animation: 'bsOrbB 18s ease-in-out infinite',
          transform: `translate3d(${tilt.x * 40}px, ${tilt.y * 40}px, 0)`,
          transition: 'transform .4s ease',
        }}
      />
      <div
        aria-hidden
        className="bs-orb"
        style={{
          position: 'absolute',
          top: '40%', left: '45%',
          width: 460, height: 460,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(120,80,200,.3) 0%, rgba(120,80,200,0) 70%)',
          filter: 'blur(80px)',
          animation: 'bsOrbC 20s ease-in-out infinite',
          transform: `translate3d(${tilt.x * 20}px, ${tilt.y * 20}px, 0)`,
          transition: 'transform .4s ease',
        }}
      />

      {/* 3D perspective grid */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
          transform: 'perspective(600px) rotateX(62deg) translateY(38vh) scale(2.6)',
          transformOrigin: 'center bottom',
          opacity: 0.35,
          maskImage: 'radial-gradient(ellipse at 50% 0%, #000 10%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 0%, #000 10%, transparent 75%)',
          animation: 'bsGrid 6s linear infinite',
        }}
      />

      {/* Film grain */}
      <div
        aria-hidden
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          opacity: 0.04, mixBlendMode: 'overlay',
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Vignette */}
      <div
        aria-hidden
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,.55) 100%)',
        }}
      />

      {/* ═══════════════ TOP BAR ═══════════════ */}
      <div
        style={{
          position: 'sticky', top: 12, marginLeft: 16, marginRight: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          zIndex: 5, gap: 10, flexWrap: 'wrap',
          paddingTop: 6,
        }}
      >
        <div
          className="bs-topbar-pill"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 14px',
            borderRadius: 999,
            background: 'rgba(10,10,10,.6)',
            border: '1px solid rgba(255,255,255,.08)',
            backdropFilter: 'blur(14px) saturate(160%)',
            WebkitBackdropFilter: 'blur(14px) saturate(160%)',
            fontSize: '.78rem',
            fontWeight: 600,
            color: '#cfcfc9',
            letterSpacing: '.02em',
            animation: mounted ? 'bsFadeIn .8s .2s both' : undefined,
          }}
        >
          <span
            style={{
              width: 6, height: 6, borderRadius: '50%',
              background: '#94e815',
              boxShadow: '0 0 8px #94e815',
              animation: 'bsBadgePulse 2s infinite',
            }}
          />
          SushiKi · Don Vito · Hürth
        </div>

        <div
          style={{
            display: 'flex',
            padding: 3,
            background: 'rgba(10,10,10,.6)',
            border: '1px solid rgba(255,255,255,.08)',
            borderRadius: 999,
            backdropFilter: 'blur(14px) saturate(160%)',
            WebkitBackdropFilter: 'blur(14px) saturate(160%)',
            animation: mounted ? 'bsFadeIn .8s .2s both' : undefined,
          }}
        >
          {(['de', 'en'] as const).map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              style={{
                padding: '7px 14px',
                background: lang === l ? '#94e815' : 'transparent',
                color: lang === l ? '#0a0a0a' : '#f4f4f0',
                border: 'none',
                borderRadius: 999,
                fontSize: '.75rem',
                fontWeight: 700,
                letterSpacing: '.08em',
                cursor: 'pointer',
                transition: 'all .25s',
              }}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* ═══════════════ CONTENT ═══════════════ */}
      <div
        ref={sheetRef}
        style={{
          position: 'relative',
          zIndex: 2,
          minHeight: 'calc(100vh - 60px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px 22px 48px',
          maxWidth: 1100,
          margin: '0 auto',
          transform: `perspective(1200px) rotateX(${tilt.y * -1.2}deg) rotateY(${tilt.x * 1.2}deg)`,
          transition: 'transform .4s ease',
        }}
      >
        {/* Table badge — glowing */}
        <div
          style={{
            position: 'relative',
            padding: '10px 26px',
            borderRadius: 999,
            background: 'linear-gradient(135deg, #94e815 0%, #6fb310 100%)',
            color: '#0a0a0a',
            fontFamily: '"Inter Tight", sans-serif',
            fontWeight: 800,
            fontSize: '.95rem',
            letterSpacing: '.05em',
            textTransform: 'uppercase',
            boxShadow: '0 10px 40px rgba(148,232,21,.35), 0 0 0 1px rgba(255,255,255,.12) inset',
            animation: mounted ? 'bsFadeUp .9s cubic-bezier(.22,1,.36,1) .1s both, bsFloat 4s ease-in-out 1s infinite' : undefined,
            marginBottom: 20,
          }}
        >
          <span
            style={{
              position: 'absolute', inset: -6, borderRadius: 999,
              border: '1px dashed rgba(148,232,21,.4)',
              animation: 'bsRingRotate 14s linear infinite',
            }}
          />
          {t('table')} {tableNumber}
        </div>

        <h1
          className="bs-hero-h1"
          style={{
            fontFamily: '"Inter Tight", sans-serif',
            fontSize: 'clamp(2.4rem, 6vw, 3.8rem)',
            fontWeight: 800,
            letterSpacing: '-0.035em',
            lineHeight: 1.05,
            margin: 0,
            textAlign: 'center',
            animation: mounted ? 'bsFadeUp 1s cubic-bezier(.22,1,.36,1) .25s both' : undefined,
            background: 'linear-gradient(180deg, #ffffff 0%, #c9c9c0 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {lang === 'de' ? 'Willkommen am Tisch' : 'Welcome to the table'}
        </h1>

        <p
          style={{
            marginTop: 12,
            marginBottom: 38,
            color: '#a1a1a1',
            fontSize: 'clamp(.95rem, 2.2vw, 1.05rem)',
            textAlign: 'center',
            maxWidth: 520,
            lineHeight: 1.55,
            animation: mounted ? 'bsFadeUp 1s cubic-bezier(.22,1,.36,1) .4s both' : undefined,
          }}
        >
          {lang === 'de'
            ? 'Zwei Küchen. Ein Erlebnis. Wählen Sie, wohin die Reise geht.'
            : 'Two kitchens. One experience. Choose where your journey begins.'}
        </p>

        {/* Brand cards */}
        <div
          className="bs-grid"
          style={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 26,
            animation: mounted ? 'bsFadeUp 1s cubic-bezier(.22,1,.36,1) .55s both' : undefined,
          }}
        >
          {/* SUSHIKI CARD */}
          <button
            type="button"
            className="bs-card"
            onClick={() => go('sushiki')}
            style={{
              ['--bs-ring' as any]: 'linear-gradient(135deg, #94e815 0%, #4d7a0b 50%, transparent 100%)',
              ['--bs-accent' as any]: '#94e815',
              position: 'relative',
              textAlign: 'left',
              padding: 28,
              minHeight: 320,
              borderRadius: 28,
              border: '1px solid rgba(255,255,255,.06)',
              background:
                'linear-gradient(145deg, rgba(148,232,21,.12) 0%, rgba(20,20,20,.85) 60%), radial-gradient(circle at 20% 10%, rgba(148,232,21,.25), transparent 55%)',
              color: '#f4f4f0',
              boxShadow: '0 30px 80px -20px rgba(0,0,0,.75), 0 0 0 1px rgba(255,255,255,.04) inset',
              cursor: 'pointer',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div className="bs-shine" />

            {/* Art — SushiKi logo lockup */}
            <div
              className="bs-art"
              style={{
                width: '100%',
                height: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                overflow: 'hidden',
                marginBottom: 22,
                filter: 'drop-shadow(0 10px 30px rgba(148,232,21,.28))',
              }}
            >
              <img
                src="/sushiki_logo_w.png?v=2026"
                alt="SushiKi"
                loading="eager"
                style={{
                  height: '100%',
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>

            <div>
              <div
                style={{
                  fontSize: '.72rem', fontWeight: 700,
                  letterSpacing: '.24em', textTransform: 'uppercase',
                  color: '#94e815',
                  marginBottom: 10,
                }}
              >
                {lang === 'de' ? 'Japanische Küche' : 'Japanese Kitchen'}
              </div>
              <div style={{ color: '#c9c9c0', fontSize: '.95rem', lineHeight: 1.5, marginBottom: 20 }}>
                {t('sushiDesc')}
              </div>

              <div
                className="bs-cta"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 18px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,.06)',
                  border: '1px solid rgba(148,232,21,.35)',
                  color: '#94e815',
                  fontSize: '.85rem', fontWeight: 700,
                  letterSpacing: '.04em',
                }}
              >
                {lang === 'de' ? 'Zur Karte' : 'View menu'}
                <span>→</span>
              </div>
            </div>
          </button>

          {/* DON VITO CARD */}
          <button
            type="button"
            className="bs-card"
            onClick={() => go('donvito')}
            style={{
              ['--bs-ring' as any]: 'linear-gradient(135deg, #e8a94b 0%, #8a5a1f 50%, transparent 100%)',
              ['--bs-accent' as any]: '#e8a94b',
              position: 'relative',
              textAlign: 'left',
              padding: 28,
              minHeight: 320,
              borderRadius: 28,
              border: '1px solid rgba(255,255,255,.06)',
              background:
                'linear-gradient(145deg, rgba(232,169,75,.14) 0%, rgba(22,14,8,.9) 60%), radial-gradient(circle at 80% 10%, rgba(232,169,75,.28), transparent 55%)',
              color: '#f5ead1',
              boxShadow: '0 30px 80px -20px rgba(0,0,0,.75), 0 0 0 1px rgba(255,255,255,.04) inset',
              cursor: 'pointer',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div className="bs-shine" />

            {/* Art — Don Vito logo lockup */}
            <div
              className="bs-art"
              style={{
                width: '100%',
                height: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                overflow: 'hidden',
                marginBottom: 22,
                filter: 'drop-shadow(0 10px 30px rgba(232,169,75,.3))',
              }}
            >
              <img
                src="/Pizza_Don_Vito_hd_light.png?v=2026"
                alt="Pizza Don Vito"
                loading="eager"
                style={{
                  height: '100%',
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>

            <div>
              <div
                style={{
                  fontSize: '.72rem', fontWeight: 700,
                  letterSpacing: '.24em', textTransform: 'uppercase',
                  color: '#e8a94b',
                  marginBottom: 10,
                }}
              >
                {lang === 'de' ? 'Italienische Küche' : 'Italian Kitchen'}
              </div>
              <div style={{ color: '#d6cdb4', fontSize: '.95rem', lineHeight: 1.5, marginBottom: 20 }}>
                {t('pizzaDesc')}
              </div>

              <div
                className="bs-cta"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 18px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,.06)',
                  border: '1px solid rgba(232,169,75,.4)',
                  color: '#e8a94b',
                  fontSize: '.85rem', fontWeight: 700,
                  letterSpacing: '.04em',
                }}
              >
                {lang === 'de' ? 'Zur Karte' : 'View menu'}
                <span>→</span>
              </div>
            </div>
          </button>
        </div>

        {/* Hint */}
        <div
          style={{
            marginTop: 34,
            color: '#6a6a66',
            fontSize: '.78rem',
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            textAlign: 'center',
            animation: mounted ? 'bsFadeIn 1.2s 1s both' : undefined,
          }}
        >
          {lang === 'de' ? 'Tippen Sie, um fortzufahren' : 'Tap to continue'}
        </div>
      </div>
    </div>
  );
};

export default BrandSelectPage;
